// src/pages/Menu/Articulacion/Admin/AnnualReportsCard.jsx
// Vista administrador — todo lo editable en una sola tarjeta, con separadores notorios y upload con progreso

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  FileText,
  Trash2,
  Edit2,
  Save,
  X,
  ArrowLeft,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  Loader2,
  Info,
} from "lucide-react";
import Swal from "sweetalert2";
import * as Svc from "../../../services/annualreportService"; // ajusta si tu path es distinto
import { useNavigate } from "react-router-dom";
import { Banner } from "../../common/Banner";
import TooltipHelper from "../../common/TooltipHelper";
import SectionTips from "../../common/SectionTips";

const ORDER_FIELD = "position";
const DEFAULT_PREVIOUS_TITLE = "REPORTES ANTERIORES";

const input =
  "w-full rounded-2xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white";
const btn =
  "inline-flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap";
const btnPri = `${btn} bg-[#0488AF] text-white hover:bg-[#037a98] disabled:opacity-60 disabled:cursor-not-allowed`;
const btnGhost = `${btn} border bg-white hover:bg-gray-50`;
const btnWarn = `${btn} border bg-white hover:bg-amber-50 text-amber-800 border-amber-300`;

const years = (() => {
  const y = new Date().getFullYear();
  const arr = [];
  for (let i = y + 1; i >= 2000; i--) arr.push(i);
  return arr;
})();

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const isValidCtaUrl = (s) => {
  if (!s) return true;
  if (s.startsWith("mailto:")) return s.slice(7).includes("@");
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const DirtyBadge = ({ show, className = "" }) =>
  show ? (
    <span
      className={`ml-2 inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 ${className}`}
    >
      Modificado
    </span>
  ) : null;

const ChipTitle = ({ children }) => (
  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
    <span className="inline-block w-1.5 h-5 bg-[#0488AF] rounded-full" />
    {children}
  </div>
);

const Divider = () => (
  <div className="my-4">
    <div className="h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent rounded-full" />
  </div>
);

/* Pequeño banner amarillo tipo tip (igual que WorktablesHome.jsx) */

/** Switch reutilizable (docs, principios, CTA) */
const Switch = ({
  checked,
  onChange,
  titleOn = "Visible para socios",
  titleOff = "Oculto para socios",
}) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
      checked ? "bg-[#0488AF]" : "bg-gray-300"
    }`}
    title={checked ? titleOn : titleOff}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

function YearModal({
  open,
  year,
  items = [],
  onClose,
  onBeginEdit,
  onRemove,
  onMove,
  onToggleVisible,
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/40" />
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            className="relative w-[min(960px,92vw)] max-h-[82vh] overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="p-5 border-b bg-gradient-to-r from-[#E6F0FF] to-[#E6FBFF]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-semibold tracking-tight">
                    Reportes {year}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {items.length} documento{items.length === 1 ? "" : "s"}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-white/70"
                >
                  <X />
                </button>
              </div>
            </div>

            <div className="p-5 overflow-y-auto space-y-3">
              {items.map((r, idx) => {
                const url =
                  r.secureUrl ||
                  (Svc.downloadAnnualReportUrl
                    ? Svc.downloadAnnualReportUrl(r.id)
                    : undefined);
                const canUp = idx > 0;
                const canDown = idx < items.length - 1;
                return (
                  <div
                    key={r.id}
                    className="group border rounded-xl p-4 flex flex-col md:flex-row md:items-start justify-between gap-4 hover:shadow-sm min-w-0"
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                        <FileText className="w-5 h-5 text-[#0488AF]" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="font-medium text-[#0488AF] hover:underline truncate break-words"
                              title={
                                r.title?.trim() ||
                                `Reporte anual ${r.year ?? year}`
                              }
                            >
                              {r.title?.trim() ||
                                `Reporte anual ${r.year ?? year}`}
                            </a>
                          ) : (
                            <span className="font-medium truncate break-words">
                              {r.title?.trim() ||
                                `Reporte anual ${r.year ?? year}`}
                            </span>
                          )}
                          {r.version && (
                            <span className="text-xs border rounded px-1.5 py-0.5 bg-gray-50 text-gray-700">
                              v{r.version}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600">
                          Posición:{" "}
                          {typeof r?.[ORDER_FIELD] === "number" &&
                          r[ORDER_FIELD] > 0
                            ? r[ORDER_FIELD]
                            : idx + 1}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 shrink-0 w-full md:w-auto mt-3 md:mt-0 md:justify-end flex-shrink-0">
                      <Switch
                        checked={!!r.visibleForMembers}
                        onChange={(v) => onToggleVisible(r.id, v)}
                      />
                      <button
                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                        onClick={() => onMove(year, r.id, -1)}
                        disabled={!canUp}
                        title="Mover arriba"
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                        onClick={() => onMove(year, r.id, +1)}
                        disabled={!canDown}
                        title="Mover abajo"
                      >
                        <ChevronDown size={16} />
                      </button>
                      <button
                        className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                        onClick={() => onBeginEdit(r)}
                        title="Editar"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-red-600 hover:bg-red-50"
                        onClick={() => onRemove(r.id)}
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function AnnualReportsCard({ onBack }) {
  const navigate = useNavigate();
  const goBack = () => {
    if (typeof onBack === "function") return onBack();
    if (window.history.length > 1) return navigate(-1);
    navigate("/profile");
  };

  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);

  // Subir
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [version, setVersion] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const fileRef = useRef(null);

  // Progreso de subida
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  // Edición
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editVersion, setEditVersion] = useState("");
  const [editYear, setEditYear] = useState(new Date().getFullYear());
  const [savingEdit, setSavingEdit] = useState(false);
  const [savingEditInline, setSavingEditInline] = useState(false);

  // Config actual
  const [cfgLoading, setCfgLoading] = useState(false);
  const [cfgSaving, setCfgSaving] = useState(false);
  const [previousTitle, setPreviousTitle] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [principlesVisible, setPrinciplesVisible] = useState(true);
  const [ctaEnabled, setCtaEnabled] = useState(false);
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [lastVisibleAt, setLastVisibleAt] = useState(null);

  // Snapshot original para diferenciar/restaurar
  const [origCfg, setOrigCfg] = useState(null);

  // Reordenamiento
  const [dirtyYears, setDirtyYears] = useState(new Set());
  const [snapshotByYear, setSnapshotByYear] = useState({});
  const [savingYearOrder, setSavingYearOrder] = useState(null);
  const [dirtyYearsState, setDirtyYearsState] = useState({});

  const [openYearModal, setOpenYearModal] = useState(null);

  // Año destacado
  const [featuredYear, setFeaturedYear] = useState(null);
  const [headline, setHeadline] = useState("");
  const [headlineOrig, setHeadlineOrig] = useState("");
  const [loadingHeadline, setLoadingHeadline] = useState(false);

  const showLoading = (title = "Procesando…") =>
    Swal.fire({
      title,
      html: "Por favor espera un momento.",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
  const askConfirm = (opts = {}) =>
    Swal.fire({
      icon: "question",
      showCancelButton: true,
      confirmButtonText: opts.confirmButtonText || "Sí, continuar",
      cancelButtonText: "Cancelar",
      allowOutsideClick: false,
      ...opts,
    });
  const toast = (title = "Listo", icon = "success", ms = 900) =>
    Swal.fire({ icon, title, timer: ms, showConfirmButton: false });

  const MAX_MB = 15;
  const formatBytes = (n) =>
    typeof n === "number" ? `${(n / 1024 / 1024).toFixed(2)} MB` : "";

  async function load(yearFilter) {
    try {
      setLoading(true);
      const data = await Svc.listAnnualReports(yearFilter);
      setList(Array.isArray(data) ? data : []);

      setCfgLoading(true);
      const cfg = await Svc.getAnnualReportsConfig();
      const current = {
        pageTitle: cfg?.pageTitle != null ? cfg.pageTitle : "",
        subtitle: cfg?.subtitle != null ? cfg.subtitle : "",
        previousTitle: cfg?.previousTitle != null ? cfg.previousTitle : "",
        principlesVisible: !!cfg?.principlesVisible,
        ctaEnabled: !!cfg?.ctaEnabled,
        ctaLabel: cfg?.ctaLabel || "",
        ctaUrl: cfg?.ctaUrl || "",
        lastVisibleAt: cfg?.lastVisibleAt || null,
      };

      setPageTitle(current.pageTitle != null ? current.pageTitle : "");
      setSubtitle(current.subtitle != null ? current.subtitle : "");
      // Si previousTitle es el valor por defecto o está vacío, mostrar vacío (el placeholder mostrará el default)
      const prevTitle = current.previousTitle != null ? current.previousTitle : "";
      setPreviousTitle(prevTitle === DEFAULT_PREVIOUS_TITLE ? "" : prevTitle);
      setPrinciplesVisible(current.principlesVisible);
      setCtaEnabled(current.ctaEnabled);
      setCtaLabel(current.ctaLabel || "");
      setCtaUrl(current.ctaUrl || "");
      setLastVisibleAt(current.lastVisibleAt || null);

      const prevTitleForOrig = prevTitle === DEFAULT_PREVIOUS_TITLE ? "" : prevTitle;
      setOrigCfg({
        pageTitle: current.pageTitle != null ? current.pageTitle : "",
        subtitle: current.subtitle != null ? current.subtitle : "",
        previousTitle: prevTitleForOrig, // Guardar el valor real, no el default
        principlesVisible: current.principlesVisible,
        ctaEnabled: current.ctaEnabled,
        ctaLabel: current.ctaLabel || "",
        ctaUrl: current.ctaUrl || "",
        lastVisibleAt: current.lastVisibleAt || null,
      });
    } catch (e) {
      console.error(e);
      Swal.fire(
        "Error",
        e?.message || "No se pudieron cargar los reportes",
        "error"
      );
    } finally {
      setLoading(false);
      setCfgLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const yearsInList = useMemo(
    () => Array.from(new Set(list.map((r) => r.year))).sort((a, b) => b - a),
    [list]
  );

  useEffect(() => {
    if (!yearsInList.length) {
      setFeaturedYear(null);
      setHeadline("");
      setHeadlineOrig("");
      return;
    }
    setFeaturedYear(yearsInList[0]);
  }, [yearsInList]);

  useEffect(() => {
    if (!featuredYear) {
      setHeadline("");
      setHeadlineOrig("");
      return;
    }
    (async () => {
      try {
        setLoadingHeadline(true);
        const res = await Svc.getAnnualReportYearTitle(featuredYear);
        const h = (res?.headline || "").trim();
        setHeadline(h);
        setHeadlineOrig(h);
      } catch {
        setHeadline("");
        setHeadlineOrig("");
      } finally {
        setLoadingHeadline(false);
      }
    })();
  }, [featuredYear]);

  const pickFile = (f) => {
    if (!f) return;
    const isPdf =
      f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");
    if (!isPdf)
      return Swal.fire(
        "Archivo inválido",
        "Solo se permiten archivos PDF.",
        "warning"
      );
    if (f.size > MAX_MB * 1024 * 1024) {
      // Si es PDF, mostrar mensaje con link de compresión
      if (isPdf) {
        return Swal.fire({
          icon: "warning",
          title: "Archivo muy grande",
          html: `El archivo "${f.name}" es demasiado grande (máx ${MAX_MB} MB).<br/><br/>Por favor, comprime el archivo PDF antes de subirlo.<br/><br/><a href="https://www.ilovepdf.com/es/comprimir_pdf" target="_blank" rel="noopener noreferrer" style="color: #0488AF; text-decoration: underline;">Comprimir PDF aquí</a>`,
          confirmButtonText: "Entendido",
        });
      }
      return Swal.fire(
        "Archivo muy grande",
        `Máximo ${MAX_MB} MB. El archivo pesa ${formatBytes(f.size)}.`,
        "warning"
      );
    }
    setFile(f);
    if (!title.trim()) {
      const base = (f.name || "").replace(/\.pdf$/i, "");
      setTitle(base);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return Swal.fire("Atención", "Selecciona un PDF", "info");
    try {
      setUploading(true);
      setUploadPct(0);

      const onProgress = (evt) => {
        if (!evt) return;
        const total = evt.total || 0;
        if (total) {
          const pct = Math.min(99, Math.round((evt.loaded / total) * 100));
          setUploadPct(pct);
        }
      };

      await Svc.uploadAnnualReport({
        file,
        title: title.trim() || undefined,
        year,
        version: (version || "").trim() || undefined,
        onProgress,
      });

      setUploadPct(100);
      await toast("Reporte subido");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setTitle("");
      setVersion("");
      setYear(new Date().getFullYear());
      await load();
    } catch (e) {
      console.error(e);
      Swal.fire(
        "Error",
        e?.message || "No se pudo subir el reporte",
        "error"
      );
    } finally {
      setUploading(false);
      setTimeout(() => setUploadPct(0), 400);
    }
  };

  const beginEdit = (r) => {
    setEditingId(r.id);
    setEditTitle(r.title || "");
    setEditVersion(r.version || "");
    setEditYear(r.year || new Date().getFullYear());
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditVersion("");
    setEditYear(new Date().getFullYear());
    setSavingEdit(false);
  };

  const saveEdit = async (id) => {
    try {
      setSavingEdit(true);
      showLoading("Guardando…");
      const payload = {
        title: (editTitle || "").trim() || undefined,
        year: Number(editYear),
        version: (editVersion || "").trim() || undefined,
      };
      await Svc.updateAnnualReport(id, payload);
      Swal.close();
      await toast("Actualizado");
      cancelEdit();
      await load();
    } catch (e) {
      Swal.close();
      console.error(e);
      Swal.fire("Error", e?.message || "No se pudo actualizar", "error");
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleVisible = async (id, visible) => {
    try {
      setSavingEditInline(true);
      await Svc.updateAnnualReport(id, { visibleForMembers: !!visible });
      await load();
    } catch (e) {
      Swal.fire(
        "Error",
        e?.message || "No se pudo cambiar visibilidad",
        "error"
      );
    } finally {
      setSavingEditInline(false);
    }
  };

  const remove = async (id) => {
    const doc = list.find((r) => r.id === id);
    const ok = await askConfirm({
      title: "Eliminar reporte",
      text: `¿Seguro que deseas eliminar "${
        doc?.title || "este reporte anual"
      }"? Esta acción no se puede deshacer.`,
      confirmButtonText: "Sí, eliminar",
    });
    if (!ok.isConfirmed) return;
    try {
      showLoading("Eliminando…");
      await Svc.deleteAnnualReport(id);
      Swal.close();
      await toast("Eliminado");
      await load();
    } catch (e) {
      Swal.close();
      console.error(e);
      Swal.fire("Error", e?.message || "No se pudo eliminar", "error");
    }
  };

  const safePos = (r) => {
    const p = Number(r?.[ORDER_FIELD]);
    return Number.isInteger(p) && p > 0 ? p : Number.POSITIVE_INFINITY;
  };

  const groupsByYear = useMemo(() => {
    const map = new Map();
    for (const r of list) {
      const arr = map.get(r.year) || [];
      arr.push(r);
      map.set(r.year, arr);
    }
    const sortInYear = (arr) =>
      [...arr].sort((a, b) => {
        const pa = safePos(a),
          pb = safePos(b);
        if (pa !== pb) return pa - pb;
        const ca = a?.createdAt ? +new Date(a.createdAt) : 0;
        const cb = b?.createdAt ? +new Date(b.createdAt) : 0;
        if (ca !== cb) return ca - cb;
        return (a.title || "")
          .toLowerCase()
          .localeCompare((b.title || "").toLowerCase());
      });
    for (const [y, arr] of map) map.set(y, sortInYear(arr));
    return map;
  }, [list]);

  const yearsSorted = useMemo(
    () => Array.from(groupsByYear.keys()).sort((a, b) => b - a),
    [groupsByYear]
  );

  const setListForYear = (y, newArr) => {
    setList((prev) => {
      const others = prev.filter((r) => r.year !== y);
      return [...others, ...newArr];
    });
  };

  const moveInYear = (y, id, direction) => {
    const currentArr = groupsByYear.get(y) || [];
    const idx = currentArr.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= currentArr.length) return;

    if (!(y in dirtyYearsState))
      setSnapshotByYear((s) => ({
        ...s,
        [y]: currentArr.map((r) => ({ ...r })),
      }));

    const arr = [...currentArr];
    const [item] = arr.splice(idx, 1);
    arr.splice(newIdx, 0, item);
    const arrWithPos = arr.map((r, i) => ({ ...r, [ORDER_FIELD]: i + 1 }));
    setListForYear(y, arrWithPos);
    setDirtyYears((prev) => new Set([...prev, y]));
    setDirtyYearsState((s) => ({ ...s, [y]: true }));
  };

  const saveYearOrder = async (y) => {
    const arr = groupsByYear.get(y) || [];
    if (!arr.length) return;
    const ok = await askConfirm({
      title: `¿Guardar nuevo orden del ${y}?`,
      text: "Se actualizarán las posiciones.",
      confirmButtonText: "Sí, guardar",
    });
    if (!ok.isConfirmed) return;
    try {
      setSavingYearOrder(y);
      showLoading("Guardando orden…");
      const ids = arr.map((r) => r.id);
      await Svc.reorderAnnualReports(y, ids);
      Swal.close();
      await toast("Orden guardado");
      setDirtyYears((prev) => {
        const next = new Set(prev);
        next.delete(y);
        return next;
      });
      setSnapshotByYear((s) => {
        const { [y]: _, ...rest } = s;
        return rest;
      });
      setDirtyYearsState((s) => {
        const { [y]: _, ...rest } = s;
        return rest;
      });
      await load();
    } catch (e) {
      Swal.close();
      console.error(e);
      Swal.fire(
        "No se guardó el orden",
        e?.message || "No se pudo guardar el nuevo orden.",
        "info"
      );
    } finally {
      setSavingYearOrder(null);
    }
  };

  const discardYearOrder = (y) => {
    const snap = snapshotByYear[y];
    if (!snap) return;
    setListForYear(
      y,
      snap.map((r) => ({ ...r }))
    );
    setDirtyYears((prev) => {
      const next = new Set(prev);
      next.delete(y);
      return next;
    });
    setSnapshotByYear((s) => {
      const { [y]: _, ...rest } = s;
      return rest;
    });
    setDirtyYearsState((s) => {
      const { [y]: _, ...rest } = s;
      return rest;
    });
  };

  const hasVisibleDocs = useMemo(
    () => list.some((r) => !!r.visibleForMembers),
    [list]
  );

  const isDirty = (key, curr, orig = origCfg) => {
    if (!orig) return false;
    const a = (curr ?? "").toString().trim();
    const b = (orig[key] ?? "").toString().trim();
    return a !== b;
  };

  const restoreField = (key) => {
    if (!origCfg) return;
    const val = origCfg[key];
    switch (key) {
      case "pageTitle":
        setPageTitle(val || "");
        break;
      case "subtitle":
        setSubtitle(val || "");
        break;
      case "previousTitle":
        setPreviousTitle(val || DEFAULT_PREVIOUS_TITLE);
        break;
      case "principlesVisible":
        setPrinciplesVisible(!!val);
        break;
      case "ctaEnabled":
        setCtaEnabled(!!val);
        break;
      case "ctaLabel":
        setCtaLabel(val || "");
        break;
      case "ctaUrl":
        setCtaUrl(val || "");
        break;
      default:
        break;
    }
  };

  const restoreAllConfig = () => {
    if (!origCfg) return;
    setPageTitle(origCfg.pageTitle || "");
    setSubtitle(origCfg.subtitle || "");
    // Restaurar previousTitle al valor original (vacío si no había valor)
    setPreviousTitle(origCfg.previousTitle || "");
    setPrinciplesVisible(!!origCfg.principlesVisible);
    setCtaEnabled(!!origCfg.ctaEnabled);
    setCtaLabel(origCfg.ctaLabel || "");
    setCtaUrl(origCfg.ctaUrl || "");
    setLastVisibleAt(origCfg.lastVisibleAt || null);
    // Restaurar también el título del año destacado
    if (featuredYear) {
      setHeadline(headlineOrig || "");
    }
  };

  // Helper para verificar si hay cambios
  const hasConfigChanges = useMemo(() => {
    if (!origCfg) return false;
    // Comparar previousTitle directamente (sin normalizar a DEFAULT_PREVIOUS_TITLE)
    const currentPrevTitle = (previousTitle || "").trim();
    const origPrevTitle = (origCfg.previousTitle || "").trim();
    
    // Verificar cambios en el título del año destacado
    const headlineChanged = featuredYear && (headline || "").trim() !== (headlineOrig || "").trim();
    
    return (
      (pageTitle || "").trim() !== (origCfg.pageTitle || "").trim() ||
      (subtitle || "").trim() !== (origCfg.subtitle || "").trim() ||
      currentPrevTitle !== origPrevTitle ||
      !!principlesVisible !== !!origCfg.principlesVisible ||
      !!ctaEnabled !== !!origCfg.ctaEnabled ||
      (ctaLabel || "").trim() !== (origCfg.ctaLabel || "").trim() ||
      (ctaUrl || "").trim() !== (origCfg.ctaUrl || "").trim() ||
      headlineChanged
    );
  }, [origCfg, pageTitle, subtitle, previousTitle, principlesVisible, ctaEnabled, ctaLabel, ctaUrl, featuredYear, headline, headlineOrig]);

  const saveTextsAndCta = async () => {
    if (ctaEnabled) {
      if (!ctaLabel.trim())
        return Swal.fire("Botón de acción", "Debes indicar el nombre del botón.", "info");
      if (!isValidCtaUrl(ctaUrl.trim()))
        return Swal.fire(
          "CTA",
          "La URL debe iniciar con http(s):// o mailto:",
          "warning"
        );
    }
    try {
      setCfgSaving(true);
      showLoading("Guardando…");
      const saved = await Svc.updateAnnualReportsConfig({
        pageTitle,
        subtitle,
        previousTitle,
        ctaEnabled,
        ctaLabel: ctaLabel.trim(),
        ctaUrl: ctaUrl.trim(),
        principlesVisible,
        lastVisibleAt: lastVisibleAt
          ? new Date(lastVisibleAt).toISOString()
          : null,
      });
      setOrigCfg({
        pageTitle: saved?.pageTitle || "",
        subtitle: saved?.subtitle || "",
        previousTitle: saved?.previousTitle || "",
        principlesVisible: !!saved?.principlesVisible,
        ctaEnabled: !!saved?.ctaEnabled,
        ctaLabel: saved?.ctaLabel || "",
        ctaUrl: saved?.ctaUrl || "",
        lastVisibleAt: saved?.lastVisibleAt || null,
      });
      Swal.close();
      await toast("Configuración guardada");
    } catch (e) {
      Swal.close();
      Swal.fire(
        "Error",
        e?.message || "No se pudo actualizar la configuración",
        "error"
      );
    } finally {
      setCfgSaving(false);
    }
  };

  const savePreviousTitle = async () => {
    try {
      setCfgSaving(true);
      showLoading("Guardando…");
      const saved = await Svc.updateAnnualReportsConfig({
        pageTitle,
        subtitle,
        previousTitle: (previousTitle || "").trim(),
        principlesVisible,
        ctaEnabled,
        ctaLabel: (ctaLabel || "").trim(),
        ctaUrl: (ctaUrl || "").trim(),
        lastVisibleAt: lastVisibleAt
          ? new Date(lastVisibleAt).toISOString()
          : null,
      });
      setOrigCfg({
        pageTitle: saved?.pageTitle || "",
        subtitle: saved?.subtitle || "",
        previousTitle: saved?.previousTitle || "",
        principlesVisible: !!saved?.principlesVisible,
        ctaEnabled: !!saved?.ctaEnabled,
        ctaLabel: saved?.ctaLabel || "",
        ctaUrl: saved?.ctaUrl || "",
        lastVisibleAt: saved?.lastVisibleAt || null,
      });
      Swal.close();
      await toast("Encabezado guardado");
    } catch (e) {
      Swal.close();
      Swal.fire("Error", e?.message || "No se pudo guardar", "error");
    } finally {
      setCfgSaving(false);
    }
  };

  const resetPreviousTitleToDefault = async () => {
    try {
      setCfgSaving(true);
      showLoading("Actualizando…");
      const saved = await Svc.updateAnnualReportsConfig({
        pageTitle,
        subtitle,
        previousTitle: DEFAULT_PREVIOUS_TITLE,
        principlesVisible,
        ctaEnabled,
        ctaLabel: (ctaLabel || "").trim(),
        ctaUrl: (ctaUrl || "").trim(),
        lastVisibleAt: lastVisibleAt
          ? new Date(lastVisibleAt).toISOString()
          : null,
      });
      setPreviousTitle(saved?.previousTitle || DEFAULT_PREVIOUS_TITLE);
      setOrigCfg({
        pageTitle: saved?.pageTitle || "",
        subtitle: saved?.subtitle || "",
        previousTitle: saved?.previousTitle || "",
        principlesVisible: !!saved?.principlesVisible,
        ctaEnabled: !!saved?.ctaEnabled,
        ctaLabel: saved?.ctaLabel || "",
        ctaUrl: saved?.ctaUrl || "",
        lastVisibleAt: saved?.lastVisibleAt || null,
      });
      Swal.close();
      await toast("Encabezado restablecido");
    } catch (e) {
      Swal.close();
      Swal.fire(
        "Error",
        e?.message || "No se pudo restablecer",
        "error"
      );
    } finally {
      setCfgSaving(false);
    }
  };

  const saveHeadline = async () => {
    if (!featuredYear) return;
    const texto = (headline || "").trim();
    try {
      showLoading("Guardando…");
      const saved = await Svc.saveAnnualReportYearTitle(featuredYear, texto);
      const h = (saved?.headline || "").trim();
      setHeadline(h);
      setHeadlineOrig(h);
      Swal.close();
      await toast("Título de año guardado");
    } catch (e) {
      Swal.close();
      Swal.fire(
        "Error",
        e?.message || "No se pudo guardar el título",
        "error"
      );
    }
  };

  const clearHeadline = async () => {
    if (!featuredYear) return;
    try {
      showLoading("Actualizando…");
      const saved = await Svc.saveAnnualReportYearTitle(featuredYear, "");
      setHeadline("");
      setHeadlineOrig((saved?.headline || "").trim());
      Swal.close();
      await toast("Se usará el título por defecto");
    } catch (e) {
      Swal.close();
      Swal.fire(
        "Error",
        e?.message || "No se pudo restablecer",
        "error"
      );
    }
  };

  const saveAllEdits = async () => {
    if (ctaEnabled) {
      if (!ctaLabel.trim())
        return Swal.fire("CTA", "Debes indicar el nombre del botón.", "info");
      if (!isValidCtaUrl(ctaUrl.trim()))
        return Swal.fire(
          "CTA",
          "La URL debe iniciar con http(s):// o mailto:",
          "warning"
        );
    }
    try {
      setCfgSaving(true);
      showLoading("Guardando…");
      const savedCfg = await Svc.updateAnnualReportsConfig({
        pageTitle,
        subtitle,
        previousTitle,
        ctaEnabled,
        ctaLabel: ctaLabel.trim(),
        ctaUrl: ctaUrl.trim(),
        principlesVisible,
        lastVisibleAt: lastVisibleAt
          ? new Date(lastVisibleAt).toISOString()
          : null,
      });
      if (featuredYear) {
        const savedHead = await Svc.saveAnnualReportYearTitle(
          featuredYear,
          (headline || "").trim()
        );
        setHeadlineOrig((savedHead?.headline || "").trim());
      }
      setOrigCfg({
        pageTitle: savedCfg?.pageTitle || "",
        subtitle: savedCfg?.subtitle || "",
        previousTitle: savedCfg?.previousTitle || "", // Guardar el valor real del backend
        principlesVisible: !!savedCfg?.principlesVisible,
        ctaEnabled: !!savedCfg?.ctaEnabled,
        ctaLabel: savedCfg?.ctaLabel || "",
        ctaUrl: savedCfg?.ctaUrl || "",
        lastVisibleAt: savedCfg?.lastVisibleAt || null,
      });
      // Actualizar el estado del formulario con los valores guardados
      setPreviousTitle(savedCfg?.previousTitle || "");
      Swal.close();
      await toast("Cambios guardados");
    } catch (e) {
      Swal.close();
      Swal.fire(
        "Error",
        e?.message || "No se pudo guardar todo",
        "error"
      );
    } finally {
      setCfgSaving(false);
    }
  };

  return (
    <div className="w-full overflow-x-hidden">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="max-w-screen-xl mx-auto px-4 py-6 space-y-6"
      >
        <button
          onClick={goBack}
          className="inline-flex items-center gap-3 px-3 py-2 bg-[#DC3465] hover:bg-[#c02e59] rounded-xl text-white"
        >
          <ArrowLeft size={18} /> Volver al menú principal
        </button>

        <Banner
          title="Cuenta pública"
          message="Sube, edita, organiza y controla visibilidad de informes. Define botón de acción y textos visibles en la página pública."
        />

        <SectionTips
          section="la sección de Cuenta pública"
          buttonTitle="¿Cómo usar esta sección?"
          tipsTitle="¿Cómo usar la Cuenta pública?"
          tipsList={[
            'En esta sección puedes subir, editar y organizar los reportes anuales (cuenta pública) por año.',
            'El documento más reciente se mostrará destacado en la página pública para los socios.',
            'Puedes controlar la visibilidad de cada documento individualmente mediante el switch "Visible para socios".',
            'Los documentos se organizan por año, y puedes reordenar dentro de cada año usando los botones ↑/↓.',
            'Puedes editar el título, versión y año de cada documento después de subirlo.',
            'En la configuración puedes personalizar los textos visibles en la página pública, como el título de página, subtítulo y encabezados.',
            'También puedes configurar un botón de acción (CTA) que aparecerá en la página pública para los socios.',
          ]}
          finalNote="Consejo: Organiza los documentos por año y mantén solo visible el más reciente para facilitar el acceso de los socios."
          color="#0488AF"
          videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766415781/Video_Tutorial_m%C3%B3dulo_Administraci%C3%B3n_-_Cuenta_P%C3%BAblica_ylgljb.mp4"
          manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412946/Manual_de_usuario_administraci%C3%B3n_bsvsyl.pdf"
        />

        {/* SUBIR + CONFIG: en dos columnas solo en pantallas muy anchas */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 xl:items-start min-w-0">
          {/* Subir */}
          <div
            className="rounded-2xl p-[1.5px] min-w-0 flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #E6F0FF 0%, #E6FBFF 100%)",
            }}
          >
            <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
              <div className="p-5">
                <h2 className="text-lg font-semibold mb-3">
                  Subir documento (PDF)
                </h2>

                <form
                  onSubmit={handleUpload}
                  className="grid gap-3 md:grid-cols-3 items-end"
                >
                    {/* Botón estilo "Seleccionar archivo" como en Statutes / Protocols */}
                  <div className="md:col-span-1 flex items-end gap-2">
                    <label className={`${btnGhost} cursor-pointer`}>
                      <Upload size={16} />
                      Seleccionar archivo
                      <input
                        ref={fileRef}
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={(e) => pickFile(e.target.files?.[0])}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                  <div className="md:col-span-3 flex items-center">
                    <TooltipHelper text={`Selecciona un archivo PDF de máximo ${MAX_MB} MB. El archivo se subirá y estará disponible para visualización y descarga. Asegúrate de que el PDF contenga toda la información relevante.`} />
                  </div>

                  {/* Vista previa del archivo seleccionado */}
                  {file && (
                    <div className="md:col-span-3">
                      <div className="flex items-center gap-3 p-3 rounded-xl border bg-slate-50">
                        <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                          <FileText className="w-5 h-5 text-[#0488AF]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">
                            {file.name}
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`${btnGhost} shrink-0`}
                          onClick={() => {
                            setFile(null);
                            if (fileRef.current) fileRef.current.value = "";
                          }}
                          disabled={uploading}
                          title="Quitar archivo"
                        >
                          <X size={16} /> Quitar
                        </button>
                      </div>
                    </div>
                  )}

                  <label className="md:col-span-3">
                    <span className="block text-sm text-gray-700 mb-1">
                      Título
                    </span>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Ej: Memoria 2024"
                      className={input}
                      disabled={uploading}
                    />
                  </label>

                  <label>
                    <span className="block text-sm text-gray-700 mb-1">
                      Versión (opcional)
                    </span>
                    <input
                      value={version}
                      onChange={(e) => setVersion(e.target.value)}
                      placeholder="Ej: 1.0"
                      className={input}
                      disabled={uploading}
                    />
                  </label>

                  <label>
                    <span className="block text-sm text-gray-700 mb-1 flex items-center gap-1">
                      Año
                      <TooltipHelper text="Año al que corresponde el reporte (no el año de publicación). Por ejemplo, si subes un reporte en 2025 pero corresponde al año 2024, selecciona 2024." />
                    </span>
                    <select
                      value={year}
                      onChange={(e) => setYear(Number(e.target.value))}
                      className={input}
                      disabled={uploading}
                    >
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="md:col-span-3 flex items-center gap-3">
                    <button
                      type="submit"
                      disabled={!file || loading || uploading}
                      className={btnPri}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="animate-spin" size={16} />{" "}
                          Subiendo… {uploadPct ? `${uploadPct}%` : ""}
                        </>
                      ) : (
                        <>
                          <Upload size={16} /> Subir documento
                        </>
                      )}
                    </button>

                    {/* Barra de progreso */}
                    {uploading && (
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden min-w-[120px]">
                        <div
                          className="h-full bg-[#0488AF] transition-all"
                          style={{ width: `${uploadPct || 25}%` }}
                        />
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* TODO EDITABLE EN UNA SOLA TARJETA */}
          <div
            className="rounded-2xl p-[2px] min-w-0 flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #DCEBFF 0%, #DDF8FF 100%)",
            }}
          >
            <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
              <div className="p-5 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    Textos visibles en Cuenta pública
                  </h3>
                  <button
                    type="button"
                    onClick={restoreAllConfig}
                    disabled={!origCfg || !hasConfigChanges}
                    className={btnWarn}
                  >
                    <X size={14} /> Descartar cambios
                  </button>
                </div>

                {/* AÑO DESTACADO */}
                <div className="rounded-xl border bg-[#F8FAFC] p-4">
                  <ChipTitle>Año destacado</ChipTitle>
                  <p className="text-xs text-gray-600 mt-1">
                    Se aplica al año más reciente con reportes:{" "}
                    <b>{featuredYear ?? "—"}</b>.
                  </p>

                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <label className="min-w-[16rem] flex-1">
                      <span className="block text-sm text-gray-700 mb-1 flex items-center gap-1">
                        Título
                        <TooltipHelper text={`Si lo dejas vacío, se usará "Informe Anual ${featuredYear || "año"}".`} />
                      </span>
                      <input
                        value={headline || ""}
                        onChange={(e) => setHeadline(e.target.value)}
                        placeholder={
                          featuredYear
                            ? `Informe Anual ${featuredYear}`
                            : "Título personalizado"
                        }
                        className={input}
                        disabled={!featuredYear || loadingHeadline}
                      />
                    </label>

                    <div className="flex flex-wrap gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={saveHeadline}
                        disabled={!featuredYear || loadingHeadline}
                        className={`${btnPri} shrink-0`}
                      >
                        <Save size={16} /> Guardar título
                      </button>
                    </div>
                  </div>
                </div>

                <Divider />

                {/* ENCABEZADOS */}
                <div className="rounded-xl border bg-[#F8FAFC] p-4">
                  <ChipTitle>Encabezados en la página pública</ChipTitle>
                  <p className="text-xs text-gray-600 mt-1">
                    Estos campos afectan el <b>Banner</b> y el separador de
                    “Reportes anteriores”.
                  </p>

                  <div className="grid gap-3 mt-3">
                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm text-gray-700 flex items-center gap-1">
                          Título de página
                          <TooltipHelper text='Si lo dejas vacío, se usará "Cuenta pública".' />
                        </label>
                      </div>
                      <input
                        className={`${input}`}
                        value={pageTitle || ""}
                        onChange={(e) => setPageTitle(e.target.value)}
                        placeholder="Cuenta pública"
                      />
                    </div>

                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm text-gray-700 flex items-center gap-1">
                          Subtítulo
                          <TooltipHelper text='Si lo dejas vacío, se usará "Descarga la cuenta pública por año.".' />
                        </label>
                      </div>
                      <input
                        className={`${input}`}
                        value={subtitle || ""}
                        onChange={(e) => setSubtitle(e.target.value)}
                        placeholder="Descarga la cuenta pública por año."
                      />
                    </div>

                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm text-gray-700 flex items-center gap-1">
                          Encabezado "Reportes anteriores"
                          <TooltipHelper text='Encabezado que aparecerá antes de la sección de reportes anteriores al año destacado. Si lo dejas vacío, se usará "Reportes Anteriores".' />
                        </label>
                      </div>
                      <input
                        className={`${input}`}
                        value={previousTitle || ""}
                        onChange={(e) => setPreviousTitle(e.target.value)}
                        placeholder={DEFAULT_PREVIOUS_TITLE}
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={savePreviousTitle}
                          disabled={cfgSaving}
                          className={btnPri}
                        >
                          <Save size={16} /> Guardar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* PRINCIPIOS & CTA */}
                <div className="rounded-xl border bg-[#F8FAFC] p-4">
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-gray-800">
                          Botón de acción
                        </div>
                        <div className="text-xs text-gray-500">
                          Añade un llamado a la acción (opcional).
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Switch
                          checked={ctaEnabled}
                          onChange={setCtaEnabled}
                          titleOn="CTA activo"
                          titleOff="CTA desactivado"
                        />
                      </div>
                    </div>

                    {ctaEnabled && (
                      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        <label className="sm:col-span-1 xl:col-span-1">
                          <span className="block text-sm text-gray-700 mb-1">
                            Nombre del botón{" "}
                          </span>
                          <input
                            className={input}
                            value={ctaLabel}
                            onChange={(e) => setCtaLabel(e.target.value)}
                            placeholder="Ver más"
                          />
                        </label>
                        <label className="sm:col-span-1 xl:col-span-2">
                          <span className="block text-sm text-gray-700 mb-1 flex items-center gap-1">
                            URL (http/https o mailto:)
                            <TooltipHelper text="URL a la que dirigirá el botón. Debe comenzar con http://, https:// o mailto: para correos electrónicos." />
                          </span>
                          <input
                            className={input}
                            value={ctaUrl}
                            onChange={(e) => setCtaUrl(e.target.value)}
                            placeholder="https://…"
                          />
                        </label>
                        {!isValidCtaUrl(ctaUrl || "") && (
                          <div className="sm:col-span-2 xl:col-span-3 text-xs text-rose-700">
                            La URL debe comenzar con http(s):// o mailto:
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 pt-3 border-t">
                      <button
                        type="button"
                        onClick={saveTextsAndCta}
                        disabled={cfgLoading || cfgSaving}
                        className={btnPri}
                      >
                        <Save size={16} /> Guardar sección
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botón Guardar todo - fuera de las secciones, al final de la tarjeta */}
              <div className="px-5 pb-5 pt-4 border-t border-gray-200">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={saveAllEdits}
                    disabled={cfgLoading || cfgSaving}
                    className={btnPri}
                  >
                    <Save size={16} /> Guardar todo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Listado por año con visibilidad y versión */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Documentos subidos</h3>
        </div>
        {loading ? (
          <div className="text-gray-500 py-6">Cargando…</div>
        ) : yearsSorted.length === 0 ? (
          <div className="text-gray-500 py-6">Sin reportes</div>
        ) : (
          <div className="space-y-6">
            {yearsSorted.map((y, yi) => {
              const arr = groupsByYear.get(y) || [];
              const isDirtyY = dirtyYears.has(y);
              const isSaving = savingYearOrder === y;
              return (
                <motion.div
                  key={y}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: yi * 0.05 }}
                >
                  <div
                    className="rounded-2xl p-[1.5px]"
                    style={{
                      background:
                        "linear-gradient(135deg, #E6F0FF 0%, #E6FBFF 100%)",
                    }}
                  >
                    <div className="rounded-2xl bg-white ring-1 ring-black/5">
                      <div className="p-5">
                        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                          <div className="flex items-center gap-3">
                            <div className="px-3 py-1.5 rounded-2xl bg-[#0B4A8F] text-white font-semibold text-lg">
                              {y}
                            </div>
                            <div className="text-sm text-gray-600">
                              {arr.length} documento
                              {arr.length === 1 ? "" : "s"}
                            </div>
                            <DirtyBadge show={isDirtyY} />
                          </div>
                          <div className="flex flex-wrap gap-2 items-center">
                            {isDirtyY ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => saveYearOrder(y)}
                                  disabled={isSaving}
                                  className={`${btnPri} px-3 py-1.5 rounded-lg`}
                                >
                                  <Save size={16} /> Guardar orden
                                </button>
                                <button
                                  type="button"
                                  onClick={() => discardYearOrder(y)}
                                  disabled={isSaving}
                                  className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                                >
                                  <X size={16} /> Descartar
                                </button>
                              </>
                            ) : (
                              <div className="text-xs text-gray-500">
                                Usa ↑/↓ para ordenar y confirma con
                                “Guardar orden”.
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3">
                          {arr.map((r, idx) => {
                            const url =
                              r.secureUrl ||
                              (Svc.downloadAnnualReportUrl
                                ? Svc.downloadAnnualReportUrl(r.id)
                                : undefined);
                            const isEditing = editingId === r.id;
                            const canMoveUp = idx > 0;
                            const canMoveDown = idx < arr.length - 1;
                            const disabledMove = isSaving;
                            return (
                              <motion.div
                                key={r.id}
                                layout
                                transition={{
                                  type: "spring",
                                  stiffness: 500,
                                  damping: 40,
                                }}
                                className="border rounded-xl p-3 flex flex-col md:flex-row md:items-start justify-between gap-3 bg-white/70 min-w-0"
                              >
                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                  <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                                    <FileText className="w-5 h-5 text-[#0488AF]" />
                                  </div>

                                  {isEditing ? (
                                    <div className="flex flex-wrap items-center gap-2">
                                      <input
                                        value={editTitle}
                                        onChange={(e) =>
                                          setEditTitle(e.target.value)
                                        }
                                        className={`${input} w-[16rem]`}
                                        placeholder="Título"
                                      />
                                      <input
                                        value={editVersion}
                                        onChange={(e) =>
                                          setEditVersion(e.target.value)
                                        }
                                        className={`${input} w-[7rem]`}
                                        placeholder="Ej: 1.0"
                                      />
                                      <select
                                        value={editYear}
                                        onChange={(e) =>
                                          setEditYear(
                                            Number(e.target.value)
                                          )
                                        }
                                        className={`${input} w-[8rem]`}
                                      >
                                        {years.map((yy) => (
                                          <option key={yy} value={yy}>
                                            {yy}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  ) : (
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2">
                                        {url ? (
                                          <a
                                            href={url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-medium text-[#0488AF] hover:underline truncate break-words"
                                            title={
                                              r.title?.trim() ||
                                              `Reporte anual ${r.year}`
                                            }
                                          >
                                            {r.title?.trim() ||
                                              `Reporte anual ${r.year}`}
                                          </a>
                                        ) : (
                                          <span className="font-medium truncate break-words">
                                            {r.title?.trim() ||
                                              `Reporte anual ${r.year}`}
                                          </span>
                                        )}
                                        {r.version && (
                                          <span className="text-xs border rounded px-1.5 py-0.5 bg-gray-50 text-gray-700">
                                            v{r.version}
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-600">
                                        Posición:{" "}
                                        {typeof r?.[ORDER_FIELD] ===
                                          "number" && r[ORDER_FIELD] > 0
                                          ? r[ORDER_FIELD]
                                          : idx + 1}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className="flex flex-wrap items-center gap-2 shrink-0 w-full md:w-auto mt-2 md:mt-0 md:justify-end flex-shrink-0">
                                  <div className="flex items-center gap-2 px-2 py-1 rounded border bg-white">
                                    <Switch
                                      checked={!!r.visibleForMembers}
                                      onChange={(v) => toggleVisible(r.id, v)}
                                    />
                                    <span className="text-xs">
                                      {r.visibleForMembers
                                        ? "Visible para socios"
                                        : "Oculto para socios"}
                                    </span>
                                  </div>
                                  {!isEditing && (
                                    <>
                                      <button
                                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                                        disabled={!canMoveUp || disabledMove}
                                        onClick={() => moveInYear(y, r.id, -1)}
                                        title="Mover arriba"
                                      >
                                        <ChevronUp size={16} />
                                      </button>
                                      <button
                                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                                        disabled={
                                          !canMoveDown || disabledMove
                                        }
                                        onClick={() => moveInYear(y, r.id, +1)}
                                        title="Mover abajo"
                                      >
                                        <ChevronDown size={16} />
                                      </button>
                                    </>
                                  )}
                                  {isEditing ? (
                                    <>
                                      <button
                                        className={`${btnGhost} px-3 py-1.5 rounded-lg disabled:opacity-60`}
                                        disabled={savingEdit}
                                        onClick={() => saveEdit(r.id)}
                                        title="Guardar"
                                      >
                                        <Save size={16} />
                                      </button>
                                      <button
                                        className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                                        onClick={cancelEdit}
                                        title="Cancelar"
                                      >
                                        <X size={16} />
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                                      onClick={() => beginEdit(r)}
                                      title="Editar"
                                    >
                                      <Edit2 size={16} />
                                    </button>
                                  )}
                                  <button
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-red-600 hover:bg-red-50"
                                    onClick={() => remove(r.id)}
                                    title="Eliminar"
                                  >
                                    <Trash2 size={16} /> Eliminar
                                  </button>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>

      <YearModal
        open={openYearModal != null}
        year={openYearModal ?? undefined}
        items={
          openYearModal != null ? groupsByYear.get(openYearModal) || [] : []
        }
        onClose={() => setOpenYearModal(null)}
        onBeginEdit={(r) => {
          setOpenYearModal(null);
          beginEdit(r);
        }}
        onRemove={(id) => remove(id)}
        onMove={(y, id, d) => moveInYear(y, id, d)}
        onToggleVisible={toggleVisible}
      />
    </div>
  );
}
